import { useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { FilterGroup } from '@/components/Filter/types';
import { TagsMenuItem } from '@/modules/corporate/modules/tags/types';
import {
  adaptSubWorkspaceIdsFilter,
  getTagFilterOption,
} from '@/modules/corporate/modules/tags/utils';
import useTags from '@/modules/corporate/modules/tags/views/TagsManager/hooks/useTags';
import useTagsWithIntegrationProvider from '@/modules/corporate/modules/tags/views/TagsManager/hooks/useTagsWithIntegrationProvider';
import {
  IntegrationProviderEnum,
  TagAvailableOnEnum,
  TagCategoryEnum,
  TagTypeEnum,
} from '@/types/schema';

import { getTagFilterKey } from '../../EntityFilterManager/utils';

export default function useTagFilters({
  availableOn,
  requiredTag,
  pause,
  isSharedItem,
  availableInLibrary,
  showProviders = false,
  fetchTagWithProviders = false,
  tagType,
  nonEditableFilterProperties,
  subWorkspaceIds,
}: {
  availableOn?: TagAvailableOnEnum[];
  requiredTag?: boolean;
  pause?: boolean;
  isSharedItem?: boolean;
  availableInLibrary?: boolean[];
  showProviders?: boolean;
  fetchTagWithProviders?: boolean;
  tagType?: TagTypeEnum[];
  subWorkspaceIds?: string[];
  nonEditableFilterProperties?: string[];
}) {
  const { t } = useTranslation(['Tags', 'Jira']);
  const [searchText, setSearchText] = useState('');
  /**
   * useTagsWithIntegrationProvider is using new tag query
   * query name: tagInfo
   * the new tag query doesnt resolve options in single and multiselect
   */
  const useTaghook =
    showProviders || fetchTagWithProviders ? useTagsWithIntegrationProvider : useTags;
  const {
    tags: allTags,
    fetchMore,
    error,
    isFetching,
  } = useTaghook({
    filters: {
      name: searchText,
      availableOn,
      requiredTag,
      excludeReceivedTags: isSharedItem,
      availableInLibrary,
      crossSubWorkspace: Boolean(subWorkspaceIds?.length),
      subWorkspaceIds: adaptSubWorkspaceIdsFilter(subWorkspaceIds),
      type: tagType,
    },
    first: showProviders || fetchTagWithProviders ? -1 : undefined,
    pause,
  });

  const filterGroups: FilterGroup[] = useMemo(() => {
    const tags = allTags.filter(
      (tag) =>
        !availableOn?.[0] ||
        !nonEditableFilterProperties?.includes(
          getTagFilterKey(getTagFilterOption(tag), availableOn[0]),
        ),
    );

    if (!tags.length) {
      return [];
    }

    const { jiraTags, lyearnTags } = tags.reduce(
      (result, tag) => {
        if (tag?.category !== TagCategoryEnum.Field) {
          result.lyearnTags.push(getTagFilterOption(tag));
          return result;
        }

        if (tag.integrationProvider?.provider === IntegrationProviderEnum.Jira) {
          result.jiraTags.push(getTagFilterOption(tag));
          return result;
        }

        return result;
      },
      { jiraTags: [] as TagsMenuItem[], lyearnTags: [] as TagsMenuItem[] },
    );

    const groups = [];

    if (jiraTags.length) {
      groups.push({
        id: 'jiraTags',
        options: jiraTags,
        label: t('Jira:JiraFields'),
      });
    }

    if (lyearnTags.length) {
      groups.push({
        id: showProviders ? 'lyearnTags' : 'tags',
        options: lyearnTags,
        label: showProviders ? t('Jira:JiraFields') : undefined,
      });
    }

    return groups;
  }, [allTags, availableOn, nonEditableFilterProperties, showProviders, t]);

  return {
    searchText,
    setSearchText,
    filterGroups,
    isFetching,
    fetchMore,
    error,
  };
}
